import { createApiClient, newIdempotencyKey, type Schemas } from '@kidova/api-client';
import { createRateGate } from './rate-limit';

export type S = Schemas;

/** The CSRF token lives in memory only (§11.9: no tokens in web storage); /v1/web/session refills it. */
const csrf: { token: string | null } = { token: null };
export function setCsrf(token: string | null): void {
  csrf.token = token;
}

type ExpiredListener = () => void;
const listeners = new Set<ExpiredListener>();
export function onSessionExpired(listener: ExpiredListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Sign-in calls answer 401 for a wrong code; that is a form error, not an expired session.
const SIGN_IN_PATHS = ['/v1/auth/', '/v1/contract/', '/v1/plans'];

/**
 * POST /v1/web/session is the sign-in itself; GET and DELETE on the same path read or end a
 * session the browser already holds, so their 401 does mean it expired.
 */
export function isSignInAttempt(path: string, method: string): boolean {
  const bare = path.replace(/^\/api/, '');
  if (bare === '/v1/web/session') return method === 'POST';
  return SIGN_IN_PATHS.some((p) => bare.startsWith(p));
}

declare global {
  interface Window {
    /** GETs public/boot.js started before the app code loaded, by path; each is used once. */
    __kdPrefetch?: Record<string, Promise<Response> | undefined>;
  }
}

const withBootPrefetch: typeof fetch = (input, init) => {
  if (input instanceof Request && input.method === 'GET' && typeof window !== 'undefined') {
    const url = new URL(input.url);
    const early = window.__kdPrefetch?.[url.pathname + url.search];
    if (early && window.__kdPrefetch) {
      window.__kdPrefetch[url.pathname + url.search] = undefined;
      return early;
    }
  }
  return fetch(input, init);
};

export const rateGate = createRateGate();

const gated: typeof fetch = async (input, init) => {
  if (input instanceof Request && input.method === 'GET') await rateGate.wait();
  const response = await withBootPrefetch(input, init);
  rateGate.observe(response);
  return response;
};

export const api = createApiClient({
  fetch: gated,
  csrfToken: () => csrf.token,
  onUnauthorized: (path, method) => {
    if (isSignInAttempt(path, method)) return;
    for (const listener of listeners) listener();
  },
});

/** Idempotency-Key header for one user action (§21.9); pass the same key when retrying it. */
export function idem(key: string = newIdempotencyKey()) {
  return { header: { 'Idempotency-Key': key } } as const;
}
